/**
 * Data layer for `invoice-status-draft-sent-paid-overdue`. Thin typed fetch wrappers over /api/v1/invoice-status-draft-sent-paid-overdue.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface InvoiceStatusDraftSentPaidOverdueRow {
	id: string;
	tenant_id: string;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListInvoiceStatusDraftSentPaidOverdueResponse {
	count: number;
	items: InvoiceStatusDraftSentPaidOverdueRow[];
}

export interface CreateInvoiceStatusDraftSentPaidOverdueBody {
	[key: string]: unknown;
}

const BASE = "/api/v1/invoice-status-draft-sent-paid-overdue";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListInvoiceStatusDraftSentPaidOverdueResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListInvoiceStatusDraftSentPaidOverdueResponse>(res);
}

export async function create(body: CreateInvoiceStatusDraftSentPaidOverdueBody): Promise<InvoiceStatusDraftSentPaidOverdueRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<InvoiceStatusDraftSentPaidOverdueRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const invoiceStatusDraftSentPaidOverdueQueryKeys = {
	all: ["invoice-status-draft-sent-paid-overdue"] as const,
	list: () => [...invoiceStatusDraftSentPaidOverdueQueryKeys.all] as const,
	detail: (id: string) => [...invoiceStatusDraftSentPaidOverdueQueryKeys.all, id] as const,
};
